import { Centrifuge, Subscription, type PublicationContext } from 'centrifuge'
import { type IProviderService } from '../IProviderService'
import { Provider, type RealtimePluginOptions } from '../../RealtimePluginOptions'
import { type TEventName } from '../../TEventName'
import { type TEvent as RealtimeEvent } from '../../TEvent'
import { AesGcmDecryptor, type RealtimeDecryptor } from '../../crypto/AesGcmDecryptor'
import { parseDsn } from './parseDsn'

export class CentrifugoService implements IProviderService {
  private centrifuge!: Centrifuge
  private project: string | null = null
  private channels: Set<string> = new Set()
  private namespaces: Map<string, string> = new Map()
  private subscriptions: Map<string, Subscription> = new Map()
  private handlers: Map<string, Map<unknown, (ctx: PublicationContext) => void>> = new Map()
  private tabUuid: string | null = null
  private decryptor: RealtimeDecryptor | null = null

  init(config: RealtimePluginOptions, tabUuid: string) {
    if (config.provider !== Provider.CENTRIFUGO_DEV_ONLY) {
      throw new Error('CentrifugoService received non-centrifugo config.')
    }

    const { url } = parseDsn(config.dsn)
    this.centrifuge = new Centrifuge(url, {})
    this.project = config.project
    this.tabUuid = tabUuid
    this.channels.clear()
    this.namespaces.clear()
    this.subscriptions.clear()
    this.handlers.clear()
  }

  setKey(keyBase64: string | null): void {
    this.decryptor = keyBase64 ? new AesGcmDecryptor(keyBase64) : null
  }

  subscribe(channel: string, namespace?: string) {
    this.channels.add(channel)

    if (namespace) {
      this.namespaces.set(channel, namespace)
    }
  }

  unsubscribe(channel: string) {
    if (!this.channels.has(channel)) return

    const prefix = `${this.namespaceFor(channel)}:${channel}:`
    for (const [key, subscription] of this.subscriptions) {
      if (!key.startsWith(prefix)) continue

      subscription.unsubscribe()
      this.centrifuge.removeSubscription(subscription)
      this.subscriptions.delete(key)
      this.handlers.delete(key)
    }
    this.channels.delete(channel)
    this.namespaces.delete(channel)
  }

  connect() {
    this.centrifuge.connect()
  }

  disconnect() {
    this.centrifuge.disconnect()
  }

  onEvent<T>(channel: string, event: TEventName, handler: (payload: T) => void): void {
    if (!this.channels.has(channel)) {
      throw new Error(`Channel "${channel}" not subscribed. Call "subscribe()" first.`)
    }
    const key = this.createEventKey(channel, event)
    let subscription = this.subscriptions.get(key)

    if (!subscription) {
      subscription = this.centrifuge.newSubscription(key)
      subscription.subscribe()
      this.subscriptions.set(key, subscription)
    }

    const wrapped = async (ctx: PublicationContext) => {
      const payload = ctx.data as unknown as RealtimeEvent<T>
      if (payload.tabUuid === this.tabUuid) {
        return
      }

      const data = this.decryptor ? await this.decryptor.decrypt(payload?.data) : payload?.data
      handler(data as T)
    }
    subscription.on('publication', wrapped)

    if (!this.handlers.has(key)) {
      this.handlers.set(key, new Map())
    }
    this.handlers.get(key)!.set(handler, wrapped)
  }

  offEvent<T>(channel: string, event: TEventName, handler: (data: T) => void) {
    const key = this.createEventKey(channel, event)
    const subscription = this.subscriptions.get(key)
    if (!subscription) return

    const wrapped = this.handlers.get(key)?.get(handler)
    if (wrapped) {
      subscription.removeListener('publication', wrapped)
      this.handlers.get(key)!.delete(handler)

      if (this.handlers.get(key)!.size === 0) {
        this.centrifuge.removeSubscription(subscription)
        this.subscriptions.delete(key)
        this.handlers.delete(key)
      }
    }
  }

  private createEventKey(channel: string, event: string): string {
    return `${this.namespaceFor(channel)}:${channel}:${event}`
  }

  /** Centrifugo takes the part before the first colon as the channel namespace. */
  private namespaceFor(channel: string): string {
    return this.namespaces.get(channel) ?? this.project ?? ''
  }
}
